<template>
  <lb-dialog
    :value="visible"
    heading="Add Team Member"
    width="520"
    :loading="loading"
    content-class="add-team-member-modal-dialog"
    data-testid="add-team-member-modal"
    @input="onDialogInput"
  >
    <template v-slot:body>
      <div class="add-team-member-modal__body" data-testid="add-team-member-modal__body">
        <form autocomplete="off" @submit.prevent>
          <v-text-field
            v-model="search"
            class="add-team-member-modal__search"
            dense
            outlined
            hide-details
            clearable
            type="search"
            name="fc-add-team-member-search"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            placeholder="Search members by name or email"
            append-icon="mdi-magnify"
            data-testid="add-team-member-modal__search"
          />
        </form>

        <div class="add-team-member-modal__list" data-testid="add-team-member-modal__list">
          <div
            v-for="(user, index) in filteredUsers"
            :key="user._id || `user-${index}`"
            class="add-team-member-modal__row"
            :class="{ 'add-team-member-modal__row--selected': isSelected(user._id) }"
            :data-testid="`add-team-member-modal__row--${user._id}`"
            @click="toggleUser(user._id)"
          >
            <v-checkbox
              :input-value="isSelected(user._id)"
              hide-details
              dense
              class="ma-0 pa-0"
              data-testid="add-team-member-modal__checkbox"
              @click.stop.prevent="toggleUser(user._id)"
            />
            <div class="add-team-member-modal__avatar" data-testid="add-team-member-modal__avatar">
              {{ getInitials(user.name) }}
            </div>
            <div class="add-team-member-modal__meta">
              <p class="add-team-member-modal__name">{{ user.name || user.displayname || 'Unknown' }}</p>
              <p class="add-team-member-modal__email">{{ user.email || '' }}</p>
            </div>
          </div>
          <div
            v-if="!filteredUsers.length"
            class="add-team-member-modal__empty"
            data-testid="add-team-member-modal__empty"
          >
            No eligible members found
          </div>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <div class="add-team-member-modal__footer" data-testid="add-team-member-modal__footer">
        <div class="add-team-member-modal__role" data-testid="add-team-member-modal__role">
          <p class="add-team-member-modal__role-label">Role</p>
          <v-radio-group
            v-model="role"
            row
            hide-details
            class="ma-0 add-team-member-modal__role-group"
            data-testid="add-team-member-modal__role-group"
          >
            <v-radio
              label="Manager"
              :value="roles.MANAGER"
              data-testid="add-team-member-modal__role--manager"
            />
            <v-radio
              label="General"
              :value="roles.GENERAL"
              data-testid="add-team-member-modal__role--general"
            />
          </v-radio-group>
        </div>
        <div class="add-team-member-modal__actions">
          <v-btn
            outlined
            class="add-team-member-modal__cancel"
            data-testid="add-team-member-modal__cancel"
            :disabled="loading"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            class="add-team-member-modal__submit"
            data-testid="add-team-member-modal__submit"
            :disabled="loading || !selectedIds.length"
            :loading="loading"
            @click="submit"
          >
            Add member
          </v-btn>
        </div>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import { TEAM_MEMBER_ROLE } from '@/modules/financialclose/utils/constants';

export default {
  name: 'AddTeamMemberModal',
  props: {
    visible: { type: Boolean, default: false },
    eligibleUsers: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  emits: ['submit', 'close', 'update:visible'],
  data() {
    return {
      search: '',
      selectedIds: [],
      role: TEAM_MEMBER_ROLE.MANAGER,
      roles: TEAM_MEMBER_ROLE
    };
  },
  computed: {
    uniqueEligibleUsers() {
      const seen = new Set();
      return (this.eligibleUsers || []).filter((user) => {
        const id = user?._id != null ? String(user._id) : '';
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },
    filteredUsers() {
      const q = (this.search || '').trim().toLowerCase();
      const list = this.uniqueEligibleUsers;
      if (!q) return list;
      return list.filter((user) => {
        const name = String(user.name || user.displayname || '').toLowerCase();
        const email = String(user.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.search = '';
        this.role = TEAM_MEMBER_ROLE.MANAGER;
        this.selectedIds = [];
      }
    }
  },
  methods: {
    getInitials(name) {
      const clean = String(name || '').trim();
      const parts = clean.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return (clean.slice(0, 2) || '?').toUpperCase();
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleUser(id) {
      if (!id) return;
      const idx = this.selectedIds.indexOf(id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
    },
    onDialogInput(val) {
      this.$emit('update:visible', val);
      if (!val) this.$emit('close');
    },
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    submit() {
      this.$emit('submit', {
        userIds: [...this.selectedIds],
        role: this.role
      });
    }
  },
  beforeDestroy() {
    this.search = '';
    this.selectedIds = [];
  }
};
</script>

<style lang="scss" scoped>
@import './AddTeamMemberModal.scss';
</style>

<style lang="scss">
/* Dialog content-class is teleported outside the component — keep Role sticky in footer */
.add-team-member-modal-dialog {
  .v-card__actions {
    display: block;
    flex: 0 0 auto;
  }
}
</style>
